import React, { useContext, useMemo } from 'react';
import { ProductTransaction, ProductTransactionType } from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { Container } from '../../../../../../../../types/container';
import { CustomFieldContext } from '../../../../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../../../../types/custom_field';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { OrderContext } from '../../../../../../../../context/OrderContext';
import { ExternalEntityType } from '../../../../../../../../types/common/entity';

interface ProductTransactionSourceCustomFieldsProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionSourceCustomFields({
  transaction,
  setTransaction,
  productMasterData: pmd,
  disabled = false,
}: ProductTransactionSourceCustomFieldsProps) {
  if (!disabled) return null;

  const { products } = useContext(ProductContext);
  const { customFields } = useContext(CustomFieldContext);
  const { orders } = useContext(OrderContext);

  const items = useMemo(() => {
    return [...customFields.values()].filter(cf => {
      if (cf.hidden) return false;
      if (cf.entityType === CustomFieldEntityType.product) return true;
      return false;
    });
  }, [customFields]);

  const selectableProducts = useMemo(() => {
    return [...(products.get(pmd?.id || '')?.values() || [])].filter(p => {
      if (p.stockLocationId !== transaction.product.fromStockLocationId) return false;
      if (transaction.product.fromBinId && p.binId !== transaction.product.fromBinId) return false;
      if (transaction.product.fromContainerId && p.containerId !== transaction.product.fromContainerId) return false;
      if (transaction.product.lotId && p.lotId !== transaction.product.lotId) return false;
      if (transaction.product.kit?.fromKitId && p.kitId !== transaction.product.kit?.fromKitId) return false;
      return true;
    });
  }, [products, transaction]);

  if (transaction.type === ProductTransactionType.inbound) return null;

  return (
    <Grid item xs={12}>
      <Grid container rowSpacing={1} columnSpacing={1}>
        {items
          .sort((a, b) => a.index - b.index)
          .map(field => {
            if (!transaction.product.fromCustomFields.get(field.id)?.value) return null;

            const values = new Set<string>(
              selectableProducts
                .map(p => p.getAllIdentifierValues().get(field.id))
                .filter(el => !!el)
                .map(el => el!),
            );

            return (
              <Grid item xs={6}>
                <DropdownSearchSelect
                  value={transaction.product.fromCustomFields.get(field.id)?.value}
                  textBuilder={value => value}
                  onSelect={value => {
                    if (!value) {
                      setTransaction(transaction => transaction.withoutFromCustomField(field.id));
                      if (transaction.type === ProductTransactionType.move) {
                        setTransaction(transaction => transaction.withoutToCustomField(field.id));
                      }
                      return;
                    }

                    if (field.entityType === CustomFieldEntityType.product) {
                      setTransaction(transaction => transaction.withFromCustomField({ ...field, value }));
                      if (transaction.type === ProductTransactionType.move) {
                        setTransaction(transaction => transaction.withToCustomField({ ...field, value }));
                      }
                    } else {
                      setTransaction(new ProductTransaction(value));
                    }
                  }}
                  items={[...values]}
                  disabled={
                    disabled ||
                    field.generated ||
                    transaction.externalIdentifier?.type === ExternalEntityType.bc_purchase_order_line
                  }
                  mandatory={field.mandatory}
                  label={field.name}
                  placeholder={field.name}
                />
              </Grid>
            );
          })}
      </Grid>
    </Grid>
  );
}
